import { Api } from '@/modules/api'

import LoadingImg from '@/assets/images/load.gif'

import { Flex } from '@/components'
import { SkeletonLoader } from '@/components/derived'
import { Layout } from '@/components/layout'

import { Brands } from './brands/Brands.component'
import { Channels } from './channels/Channels.component'
import { CompanyClippers } from './company-clippers/CompanyClippers.component'
import { Publishers } from './publishers/Publishers.component'
import { Spokespersons } from './spokespeople/Spokespeople.component'
import { ThemeGroups } from './theme-groups/ThemeGroups.component'
import { Themes } from './themes/Themes.component'
import { KeyMessage } from './key-message/KeyMessage.component'

import { Routes } from '@/configs'
import { useRouting } from '@/hooks'
import { Right } from '@icon-park/react'
import { CompanyResponse } from '@shared/models'
import { useDialog } from '@/modules/dialog/Dialog.context'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { CompanyDetailsContextProvider, useCompanyContext } from './CompanyDetails.context'
import {
  CompetitorFlag,
  DetailsContainer,
  GovernmentalFlag,
  Keyword,
  KeywordsContainer,
  LogoImage,
  ViewMoreButton,
} from './CompanyDetails.styles'
import { CompanyCompetitors } from './competitors/CompanyCompetitors.component'
import { Prompt } from './prompt_rules/Prompt.component'
import { PublisherPersons } from './publisher-person/PublisherPersons.component'

export function CompanyDetails() {
  return (
    <CompanyDetailsContextProvider>
      <Component />
    </CompanyDetailsContextProvider>
  )
}

function CompanyLogo({ id }: { id?: string }): JSX.Element | null {
  if (id === 'default-logo-color') {
    return <LogoImage alt="logo" src={process.env.REACT_APP_DEFAULT_LOGO_COLOR} />
  }
  if (id === 'default-logo-gray') {
    return <LogoImage alt="logo" src={process.env.REACT_APP_DEFAULT_LOGO_GRAY} />
  }

  const [, , logo] = Api.Query<string>(`files/${id}`)
  return <LogoImage alt="logo" src={logo ?? LoadingImg} />
}

function Component(): JSX.Element | null {
  const { companyID, concurrentID } = useParams<{ companyID: string; concurrentID: string }>()
  const { isLoadingCompany, company } = useCompanyContext()
  const [loadConcurrent, isLoadingConcurrent, concurrent] = Api.LazyQuery<CompanyResponse>(
    `companies/${companyID}/${concurrentID}`,
  )
  const { navigate } = useRouting()

  const entity = concurrentID && concurrent ? concurrent : company
  const isLoading = concurrentID ? isLoadingConcurrent : isLoadingCompany

  function handleNavigationToMotherCompany(): void {
    navigate(Routes.Home.path)
    setTimeout(() => {
      navigate(Routes.CompanyDetails.path, company!.motherID!)
    }, 0)
  }

  useEffect(() => {
    if (concurrentID) {
      loadConcurrent()
    }
  }, [concurrentID])

  const allKeywords =
    entity?.keywords
      ?.split(',')
      .map((kw) => kw.trim())
      .filter((kw) => kw.length > 0) || []

  const { open } = useDialog({
    title: 'Palavras-chaves',
    content: (
      <KeywordsContainer>
        {allKeywords.map((keyword) => (
          <Keyword key={keyword}>#{keyword}</Keyword>
        ))}
      </KeywordsContainer>
    ),
    closeText: 'Fechar',
  })

  return (
    <>
      <Layout.Root>
        <Layout.Padder>
          {isLoading ?
            <SkeletonLoader amount={10} />
          : entity ?
            <Flex $type="column" $justify="flex-start" $align="stretch" $gap={16}>
              <DetailsContainer>
                <Flex $type="column" $justify="flex-start" $align="flex-start" $gap={24} $fit>
                  <h1>{entity.name}</h1>
                  <KeywordsContainer>
                    {allKeywords.slice(0, 3).map((keyword) => (
                      <Keyword key={keyword}>#{keyword}</Keyword>
                    ))}
                  </KeywordsContainer>
                  <Flex $type="row" $justify="flex-start" $align="center" $gap={16} $fit>
                    {entity.isGovernmental && <GovernmentalFlag />}
                    {entity.motherID && <CompetitorFlag onClick={handleNavigationToMotherCompany} />}
                    {allKeywords.length > 3 && <ViewMoreButton onClick={open}>Ver mais...</ViewMoreButton>}
                  </Flex>
                </Flex>
                <Flex $type="column" $justify="flex-start" $align="flex-end" $gap={16} $fit>
                  <CompanyLogo id={entity.systemLogoID} />
                  <Flex $type="row" $align="center" $gap={8} onClick={() => navigate(Routes.UpdateCompany, entity.id)}>
                    <h4 className="link-text">{'Atualizar dados da empresa'}</h4>
                    <Right className="link-text" size={16} />
                  </Flex>
                </Flex>
              </DetailsContainer>

              <Brands />
              <KeyMessage />
              <ThemeGroups />
              <Themes />
              <Channels />
              <Spokespersons />
              <Publishers />
              <PublisherPersons />
              {!entity.motherID && <CompanyClippers />}
              {entity.hasCompetitors && <CompanyCompetitors />}
              <Prompt />
            </Flex>
          : null}
        </Layout.Padder>
      </Layout.Root>
    </>
  )
}
